import React, { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { Link } from 'react-router-dom';
import {
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';

import { Block } from '@klp/design/Block';
import type { IconName, PictogramNameType } from '@klp/design/Illustrations';
import { Illustration } from '@klp/design/Illustrations';
import type { IUser } from '@klp/user/interfaces';

import type { SidebarMenuItem } from './classes/SidebarMenuItem';

interface Props {
    icon?: IconName;
    pictogram?: PictogramNameType;
    translation?: string;
    nestedItems: Array<SidebarMenuItem>;
    LinkComponent: typeof Link;
    drawerCallback: () => void;
    routeHighlight: (route: string) => void;
    user?: IUser;
}

export const NestedSidebarMenuItem = ({
    icon,
    pictogram,
    translation,
    nestedItems,
    LinkComponent,
    drawerCallback,
    routeHighlight,
    user,
}: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const renderItemWithAccess = (element: SidebarMenuItem) =>
        element.access && element.access(user) && renderMenuItem(element);

    const renderItemWithCondition = (element: SidebarMenuItem) =>
        element.condition && renderMenuItem(element);

    const renderMenuItem = (element: SidebarMenuItem) => {
        return (
            <Fragment key={element.route}>
                {element.divider && (
                    <Block marginVertical="compact">
                        <Divider />
                    </Block>
                )}
                {!element.divider && (
                    <ListItem
                        button={true}
                        {...({
                            component: LinkComponent,
                            to: element.route,
                        } as any)}
                        selected={routeHighlight(element.matchRoute || '')}
                        onClick={() => {
                            drawerCallback();
                            if (element?.callback) {
                                element?.callback();
                            }
                        }}
                    >
                        <ListItemIcon>
                            {element.pictogram && (
                                <Illustration pictogram={element.pictogram} />
                            )}
                            {element.icon && (
                                <Illustration
                                    icon={element.icon}
                                    klpColor="main"
                                    klpPalette="primary"
                                />
                            )}
                        </ListItemIcon>
                        <ListItemText primary={t(element.translation || '')} />
                        {element.endIcon && (
                            <Illustration
                                icon={element.endIcon}
                                klpColor="main"
                                klpPalette="primary"
                            />
                        )}
                    </ListItem>
                )}
            </Fragment>
        );
    };

    return (
        <Fragment>
            <ListItem button={true} onClick={() => setOpen(!open)}>
                <ListItemIcon>
                    {pictogram && <Illustration pictogram={pictogram} />}
                    {icon && (
                        <Illustration
                            icon={icon}
                            klpColor="main"
                            klpPalette="primary"
                        />
                    )}
                </ListItemIcon>
                <ListItemText primary={t(translation || '')} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            {open && (
                <List component="nav">
                    {nestedItems?.map((element, key) => (
                        <Fragment key={key}>
                            {element.access && renderItemWithAccess(element)}
                            {element.condition &&
                                renderItemWithCondition(element)}
                        </Fragment>
                    ))}{' '}
                </List>
            )}
        </Fragment>
    );
};
